/**
 * addDataConfig — the single typed registry for the global "+ Add data" menu
 * and the app-wide document intake (drag-and-drop → Document → Stories).
 *
 * Why this exists (See the work): getting work INTO Avium used to require
 * knowing the right page — the Doc → Stories flow in particular sat behind
 * Workload → Data Sources → "Spreadsheet / File" → a tab. The Add-data menu
 * (Header) and the global drop target (DocImportModal) make every intake path
 * reachable from any page; this module is the one place their vocabulary,
 * targets, and file rules live.
 */

import { AppEvent } from './appEvents';

/** Fired (with an optional `detail.file`) to open the Doc → Stories modal anywhere. */
export const OPEN_DOC_IMPORT_EVENT = 'avium:open-doc-import';

/** Fired after Doc → Stories commits, so mounted boards/dashboards refetch.
 *  Re-exported from the canonical {@link AppEvent} registry; kept as a named
 *  alias so existing `import { TICKETS_CREATED_EVENT }` call sites still work. */
export const TICKETS_CREATED_EVENT = AppEvent.TicketsCreated;

/** Extensions the Doc → Stories extractor accepts (mirrors server docImport). */
export const DOC_IMPORT_EXTENSIONS = ['docx', 'txt', 'md'] as const;

export const isDocImportFile = (name: string): boolean => {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return (DOC_IMPORT_EXTENSIONS as readonly string[]).includes(ext);
};

/** Open the Doc → Stories modal from anywhere (optionally pre-loaded with a file). */
export const openDocImport = (file?: File) =>
  window.dispatchEvent(new CustomEvent(OPEN_DOC_IMPORT_EVENT, { detail: file ? { file } : undefined }));

export interface AddDataItem {
  key: string;
  icon: string;
  /** Menu row title — a verb phrase (what the user does, not what we call it). */
  label: string;
  /** One-line description under the title. */
  description: string;
  /** 'navigate' → route via react-router; 'doc-import' → open the global modal. */
  action: { kind: 'navigate'; to: string } | { kind: 'doc-import' };
}

export const ADD_DATA_ITEMS: AddDataItem[] = [
  {
    key: 'ticket',
    icon: '✦',
    label: 'Create a ticket',
    description: 'Add a Story, Task, or Bug straight onto the board.',
    action: { kind: 'navigate', to: '/pipeline?create=1' },
  },
  {
    key: 'doc',
    icon: '📝',
    label: 'Upload meeting notes',
    description: 'Drop in a transcript or requirements doc — Avium drafts the Stories. Or drag one onto any page.',
    action: { kind: 'doc-import' },
  },
  {
    key: 'sheet',
    icon: '📊',
    label: 'Import a spreadsheet',
    description: 'CSV or Excel from any tool — map columns, done.',
    action: { kind: 'navigate', to: '/import' },
  },
  {
    key: 'source',
    icon: '🔌',
    label: 'Connect a source',
    description: 'Jira, Azure DevOps or Asana — live sync into one workspace.',
    action: { kind: 'navigate', to: '/connect' },
  },
];

// ── Doc → Stories description template ───────────────────────────────────────
// Mirrors server/src/lib/docExtract.ts TEMPLATE_HEADINGS — the structured
// sections extraction lands in a ticket's Description (H2 per section;
// checklists for work sections, plain bullets for facts).

export interface DocTemplateSection {
  heading: string;
  icon: string;
  /** Placeholder/teaching copy for an empty section editor. */
  hint: string;
  /** Work sections become "- [ ]" checklists in the description; facts stay "-" bullets. */
  checklist: boolean;
}

export const DOC_TEMPLATE_SECTIONS: DocTemplateSection[] = [
  { heading: 'Due dates',    icon: '📅', hint: 'Dates the document commits to — one per line', checklist: false },
  { heading: 'Requirements', icon: '✅', hint: 'What must be true when this is done — one per line', checklist: true },
  { heading: 'Action items', icon: '🎯', hint: 'The concrete next steps — one per line', checklist: true },
  { heading: 'Milestones',   icon: '🚩', hint: 'Named checkpoints along the way — one per line', checklist: false },
  { heading: 'Open questions', icon: '❓', hint: 'Unresolved questions the team needs answered — one per line', checklist: true },
  { heading: 'Notes',        icon: '📝', hint: 'Context worth keeping with the ticket — one per line', checklist: false },
];

export const docTemplateSection = (heading: string): DocTemplateSection | undefined =>
  DOC_TEMPLATE_SECTIONS.find(s => s.heading.toLowerCase() === heading.toLowerCase());

/**
 * Normalize a section editor's markdown into the server's lines[] shape:
 * one entry per paragraph/line, with any leading marker the editor's markdown
 * shortcuts produced stripped — bullets (`-`/`*`/`+`), ordered numbers
 * (`1.`/`1)`), task checkboxes (`[ ]`/`[x]`), and headings (`#`…`######`).
 * The section IS the list, so buildDescription adds the bullets/checkboxes
 * server-side; a stray heading would otherwise leak as literal `## text`.
 * Empty lines drop.
 */
export const docSectionLines = (md: string): string[] =>
  md.split(/\n+/)
    .map(l => l.trim().replace(/^(?:#{1,6}|[-*+]|\d+[.)])\s+(?:\[[ x]\]\s+)?/, ''))
    .filter(Boolean);
